'use client';

import { useEffect, useRef } from 'react';

import styles from './ScrollProgressBar.module.css';

/* Reading-progress bar pinned to the top of the viewport.
 *
 * The fill is written straight to the DOM through a ref, and the component
 * holds no state at all. It used to setState once per animation frame, which
 * re-rendered this component — and everything React re-created underneath it —
 * about sixty times a second while the reader scrolled. That is what made the
 * bar itself feel laggy. It is rendered as a SIBLING of the article, not a
 * wrapper, so its rAF loop cannot re-render the article either.
 *
 * scrollHeight is read once per resize rather than per frame: reading it in
 * the scroll handler forces a layout on every frame, which is the other half
 * of the cost. */
const ScrollProgressBar = () => {
  const fillRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let total = 0;
    const measure = () => {
      total = document.documentElement.scrollHeight - window.innerHeight;
    };

    const paint = () => {
      const el = fillRef.current;
      if (!el) return;
      const p =
        total <= 0 ? 0 : Math.min(1, Math.max(0, window.scrollY / total));
      el.style.transform = `scaleX(${p})`;
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        paint();
      });
    };
    const onResize = () => {
      measure();
      paint();
    };

    measure();
    paint();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    /* The page grows as images and figures settle, so the denominator has to
       be re-measured when it does — otherwise the bar reaches 100% early and
       then sticks. */
    const ro = new ResizeObserver(onResize);
    ro.observe(document.documentElement);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className={styles.wrap} aria-hidden="true">
      <div className={styles.track}>
        <span className={styles.fill} ref={fillRef} />
      </div>
    </div>
  );
};

export default ScrollProgressBar;
